import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle 401 errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth
export const login = (email, password) => 
    api.post('/auth/login', { email, password });

export const getMe = () => api.get('/auth/me');

export const changePassword = (currentPassword, newPassword) =>
    api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (userId, data) => api.put(`/users/${userId}`, data);
export const deleteUser = (userId) => api.delete(`/users/${userId}`);
export const resetUserPassword = (userId) => api.post(`/users/${userId}/reset-password`);

// Products (now using product_key)
export const getProducts = (params) => api.get('/products', { params });
export const getProductFilters = () => api.get('/products/filters');
export const getProduct = (productKey) => api.get(`/products/${productKey}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (productKey, data) => api.put(`/products/${productKey}`, data);
export const deleteProduct = (productKey) => api.delete(`/products/${productKey}`);

export const getProductStats = () => api.get('/products/stats');
export const getRecentProducts = (limit = 10) => api.get('/products/recent', { params: { limit } });

// Product sub-resources
export const getProductPricing = (productKey) => api.get(`/products/${productKey}/pricing`);
export const addProductPricing = (productKey, data) => api.post(`/products/${productKey}/pricing`, data);
export const getProductPackaging = (productKey) => api.get(`/products/${productKey}/packaging`);
export const updateProductPackaging = (productKey, data) => api.put(`/products/${productKey}/packaging`, data);
export const getProductSpecifications = (productKey) => api.get(`/products/${productKey}/specifications`);
export const updateProductSpecifications = (productKey, data) => api.put(`/products/${productKey}/specifications`, data);

// Categories & Filters
export const getCategories = () => api.get('/categories');
export const getCategoryHierarchy = () => api.get('/categories/hierarchy');
export const getBrands = () => api.get('/brands');
export const getDecors = () => api.get('/decors');
export const getThicknesses = () => api.get('/thicknesses');

// Search
export const getRecentSearches = () => api.get('/search/recent');

// Files
export const getProductFiles = (productKey) => api.get(`/products/${productKey}/files`);
export const uploadFile = (productKey, mediaType, file, isPrimary = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/products/${productKey}/files?media_type=${mediaType}&is_primary=${isPrimary}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};
export const deleteFile = (fileId) => api.delete(`/files/${fileId}`);
export const getFileUrl = (fileId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/files/${fileId}/download?auth=${token}`;
};

// Import - Multi-table
export const previewImport = (file, tableType = 'products_master') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/import/preview?table_type=${tableType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const importProductsMaster = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/products_master', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

// Product Image Import
export const importProductImages = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/product-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};
export const getUnmatchedImages = () => api.get('/import/unmatched-images');
export const matchUnmatchedImage = (docId, productKey) => api.post(`/import/unmatched-images/${docId}/match`, { product_key: productKey });
export const dismissUnmatchedImage = (productCode) => api.delete(`/import/unmatched-images/${encodeURIComponent(productCode)}`);

export const importPricingMaster = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/pricing_master', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const importPackagingMaster = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/packaging_master', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const importSpecificationsMaster = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/specifications_master', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const importMediaMaster = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/media_master', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const importCategoryTaxonomy = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/category_taxonomy', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const importLookupReference = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/lookup_reference', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const validateImport = () => api.post('/import/validate');
export const clearSampleData = () => api.delete('/import/clear-sample-data');

// ============ Quotation Engine ============

// Configurator
export const resolveVariants = (selected, search = '', page = 0) => api.post('/configurator/resolve', { selected, search, page, page_size: 30 });

// Quotes
export const getQuotes = (params) => api.get('/quotes', { params });
export const getQuote = (id) => api.get(`/quotes/${id}`);
export const createQuote = (data) => api.post('/quotes', data);
export const updateQuote = (id, data) => api.put(`/quotes/${id}`, data);
export const deleteQuote = (id) => api.delete(`/quotes/${id}`);

// Quote Lines
export const addQuoteLine = (quoteId, line) => api.post(`/quotes/${quoteId}/lines`, line);
export const updateQuoteLine = (quoteId, lineId, data) => api.put(`/quotes/${quoteId}/lines/${lineId}`, data);
export const deleteQuoteLine = (quoteId, lineId) => api.delete(`/quotes/${quoteId}/lines/${lineId}`);

// Quote Versioning
export const reviseQuote = (quoteId, data) => api.post(`/quotes/${quoteId}/revise`, data);
export const updateQuoteStatus = (quoteId, data) => api.put(`/quotes/${quoteId}/status`, data);

// Quote Settings
export const getQuoteSettings = () => api.get('/quote-settings');
export const updateQuoteSettings = (data) => api.put('/quote-settings', data);

// Custom Products
export const getCustomProducts = (params) => api.get('/custom-products', { params });
export const getCustomProduct = (id) => api.get(`/custom-products/${id}`);
export const createCustomProduct = (data) => api.post('/custom-products', data);
export const updateCustomProduct = (id, data) => api.put(`/custom-products/${id}`, data);
export const deleteCustomProduct = (id) => api.delete(`/custom-products/${id}`);
export const exportCustomProducts = () => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/custom-products/export?auth=${token}`;
};

// PDF Export
export const getQuotePdfUrl = (quoteId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/quotes/${quoteId}/export-pdf?auth=${token}`;
};

// Duplicate Quote
export const duplicateQuote = (quoteId) => api.post(`/quotes/${quoteId}/duplicate`);

// Admin Categories
export const getAdminCategories = () => api.get('/admin/categories');
export const createCategory = (data) => api.post('/admin/categories', data);
export const updateCategory = (id, data) => api.put(`/admin/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/admin/categories/${id}`);

// Admin Permissions
export const getPermissionDefaults = () => api.get('/admin/permissions/defaults');
export const getUserPermissions = () => api.get('/admin/permissions/users');
export const updateUserPermissions = (userId, data) => api.put(`/admin/permissions/users/${userId}`, data);

// Sheet Optimizer
export const calculateSheetOptimization = (data) => api.post('/sheet-optimizer/calculate', data);
export const getOptimizerProducts = (params) => api.get('/sheet-optimizer/products', { params });
export const getGrainSettings = () => api.get('/sheet-optimizer/grain-settings');
export const updateGrainSettings = (data) => api.put('/sheet-optimizer/grain-settings', data);
export const addGrainLockedDecor = (data) => api.post('/sheet-optimizer/grain-settings/add', data);
export const removeGrainLockedDecor = (decor) => api.delete(`/sheet-optimizer/grain-settings/${encodeURIComponent(decor)}`);

// Delivery Challans
export const getChallans = (params) => api.get('/challans', { params });
export const getChallan = (id) => api.get(`/challans/${id}`);
export const createChallan = (data) => api.post('/challans', data);
export const updateChallan = (id, data) => api.put(`/challans/${id}`, data);
export const deleteChallan = (id) => api.delete(`/challans/${id}`);
export const addChallanLine = (challanId, data) => api.post(`/challans/${challanId}/lines`, data);
export const updateChallanLine = (challanId, lineId, data) => api.put(`/challans/${challanId}/lines/${lineId}`, data);
export const deleteChallanLine = (challanId, lineId) => api.delete(`/challans/${challanId}/lines/${lineId}`);
export const getChallanSummary = () => api.get('/challans/summary');
export const getChallanPdfUrl = (challanId) => {
    const token = localStorage.getItem('token');
    return `${api.defaults.baseURL}/challans/${challanId}/export-pdf?auth=${token}`;
};

// Global search, activity, completeness, notes, exports
export const globalSearch = (q) => api.get('/global-search', { params: { q, limit: 8 } });
export const getDataCompleteness = () => api.get('/data-completeness');
export const getRecentActivity = (limit = 20) => api.get('/activity/recent', { params: { limit } });
export const updateInternalNotes = (entityType, entityId, notes) => api.put(`/internal-notes/${entityType}/${entityId}`, { internal_notes: notes });
export const duplicateCustomProduct = (id) => api.post(`/custom-products/${id}/duplicate`);
export const duplicateChallan = (id) => api.post(`/challans/${id}/duplicate`);
export const getQuotesCsvUrl = () => {
    const token = localStorage.getItem('token');
    return `${api.defaults.baseURL}/quotes/export-csv?auth=${token}`;
};
export const getChallansCsvUrl = () => {
    const token = localStorage.getItem('token');
    return `${api.defaults.baseURL}/challans/export-csv?auth=${token}`;
};

// Config Options
export const getConfigTypes = () => api.get('/config');
export const getConfigOptions = (configType) => api.get(`/config/${configType}`);
export const updateConfigOptions = (configType, values) => api.put(`/config/${configType}`, { values });
export const addConfigValue = (configType, label) => api.post(`/config/${configType}/add-value`, { label });
export const getCountries = () => api.get('/config/countries');

// Customers
export const getCustomers = (params = {}) => api.get('/customers', { params });
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);
export const getCustomerHistory = (id) => api.get(`/customers/${id}/history`);
export const exportCustomers = (data) => api.post('/customers/export', data, { responseType: 'blob' });
export const customerTypeahead = (q) => api.get('/customers/search/typeahead', { params: { q } });
export const getMigrationPreview = () => api.get('/customers/migrate/preview');
export const confirmMigration = (data) => api.post('/customers/migrate/confirm', data);


export default api;
